'use client';

import dynamic from 'next/dynamic';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import CardLocation from '@alltrails/amplitude/enums/CardLocation';
import TrailDetailsShareOption from '@alltrails/amplitude/enums/TrailDetailsShareOption';
import logTrailDetailsActionTapped from '@alltrails/amplitude/events/logTrailDetailsActionTapped';
import logTrailDetailsAddToListButtonTapped from '@alltrails/amplitude/events/logTrailDetailsAddToListButtonTapped';
import logTrailDetailsShareThisTrailOptionClicked from '@alltrails/amplitude/events/logTrailDetailsShareThisTrailOptionClicked';
import useUser from '@alltrails/context/hooks/useUser';
import { isSubscriber } from '@alltrails/context/utils/isSubscriber';
import { PageStrings } from '@alltrails/core/constants/pageStringHelpers';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import { wrapUrlSafe } from '@alltrails/core/utils/wrapUrlSafe';
import Button from '@alltrails/denali/components/Button';
import Heart from '@alltrails/denali/icons/Heart';
import HeartFilled from '@alltrails/denali/icons/HeartFilled';
import Map from '@alltrails/denali/icons/Map';
import Share from '@alltrails/denali/icons/Share';
import { useExperiments, useExposureEvent } from '@alltrails/experiments';
import useReduxListItems from '@alltrails/lists/hooks/useReduxListItems';
import { getTrailShareObject, TrailPage } from '@alltrails/trails';
import type TrailDetails from '@alltrails/types/TrailDetails';
import useIsTrailMapOpen from '@/hooks/useIsTrailMapOpen';
import styles from './MobileFooter.module.scss';

const HitTheTrailModal = dynamic(() => import('@/components/HitTheTrailModal/HitTheTrailModal'), { ssr: false });
const SignUpModal = dynamic(() => import('@alltrails/user-auth/components/SignUpModal'), { ssr: false });
const ShareModal = dynamic(() => import('@alltrails/sharing/components/ShareModal/ShareModal'), { ssr: false });

type Props = {
  trailDetails: TrailDetails;
  trailPage: TrailPage;
  isScrollLimitActive?: boolean;
};

const reduxListsAnalyticsInfo = { detailedCardLocation: 'mobile web trail details cta bar' as CardLocation, page: PageStrings.TRAIL_PAGE };

// https://www.figma.com/design/oevB4bXh8KLLDe1Yp8rBW2/Trail-Page-Web-%5BHandoff%5D?node-id=15304-253213&m=dev
export default function MobileFooter({ trailDetails, trailPage }: Props) {
  const intl = useIntl();
  const [isSignUpModalOpen, setIsSignUpModalOpen] = useState(false);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const { openTrailMap } = useIsTrailMapOpen();
  const user = useUser();
  const languageRegionCode = useLanguageRegionCode();
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const { isFavorite, handleFavoriteClick, listModal, signUpModal } = useReduxListItems(
    undefined,
    false /* preventAutoInitialize */,
    reduxListsAnalyticsInfo
  );

  const experiments = useExperiments();

  const sendShareCtaExposure = useExposureEvent('web-growth-rm-mweb-unauth-share-cta');
  const isShareCtaTreatment = !user && experiments['web-growth-rm-mweb-unauth-share-cta']?.value === 'treatment';

  useEffect(() => {
    if (!user) {
      sendShareCtaExposure();
    }
  }, [sendShareCtaExposure, user]);

  const sendSaveCtaExposure = useExposureEvent('web-growth-rm-mweb-save-cta');
  const isSaveCtaTreatment = user && !isSubscriber(user) && experiments['web-growth-rm-mweb-save-cta']?.value === 'treatment';
  const isTrailFavorite = isFavorite('trail', trailDetails.id);

  useEffect(() => {
    if (user && !isSubscriber(user)) {
      sendSaveCtaExposure();
    }
  }, [sendSaveCtaExposure, user]);

  const shareObject = useMemo(() => getTrailShareObject(trailDetails), [trailDetails]);

  const onShareClick = useCallback(() => {
    logTrailDetailsActionTapped({
      trail_id: trailDetails.id.toString(),
      trail_page_action: 'share',
      trail_page_location: 'mobile cta bar',
      trail_page_type: 'trail detail page'
    });

    const title = intl.formatMessage({ defaultMessage: '{name} on AllTrails' }, { name: shareObject.name });
    const params = new URLSearchParams();

    if (user?.referralCode) {
      params.set('sh', user.referralCode);
    } else {
      // Identifies share links from unauthenticated users for virality attribution
      params.set('sh', 'true');
    }

    const search = params.toString();
    const url = window.location.origin + wrapUrlSafe(`/${shareObject.slug}${search.length > 0 ? `?${search}` : ''}`, languageRegionCode);

    try {
      navigator.share({ title, text: title, url });
    } catch {
      setIsShareModalOpen(true);
    }
  }, [intl, languageRegionCode, shareObject.name, shareObject.slug, trailDetails.id, user?.referralCode]);

  return (
    <div className={styles.footer}>
      {isShareCtaTreatment ? (
        <Button icon={{ Component: Share }} onClick={onShareClick} text={<FormattedMessage defaultMessage="Share" />} variant="accent" />
      ) : isSaveCtaTreatment ? (
        <Button
          icon={isTrailFavorite ? <HeartFilled /> : <Heart />}
          onClick={() => {
            logTrailDetailsActionTapped({ trail_id: trailDetails.id.toString(), trail_page_action: 'save', trail_page_location: 'mobile cta bar' });
            logTrailDetailsAddToListButtonTapped({ trail_id: trailDetails.id });
            handleFavoriteClick('trail', trailDetails.id);
          }}
          text={isTrailFavorite ? <FormattedMessage defaultMessage="Saved" /> : <FormattedMessage defaultMessage="Save" />}
          variant={isTrailFavorite ? 'default' : 'accent'}
        />
      ) : (
        <Button
          onClick={() => {
            logTrailDetailsActionTapped({
              trail_id: trailDetails.id.toString(),
              trail_page_action: 'download overflow menu button',
              trail_page_location: 'mobile cta bar',
              trail_page_type: 'trail detail page'
            });
            setIsDownloadModalOpen(true);
          }}
          text={<FormattedMessage defaultMessage="Download" />}
          variant="accent"
        />
      )}
      <Button
        icon={isShareCtaTreatment || isSaveCtaTreatment ? { Component: Map } : undefined}
        onClick={() => {
          logTrailDetailsActionTapped({
            trail_id: trailDetails.id.toString(),
            trail_page_action: 'open map',
            trail_page_location: 'mobile cta bar',
            trail_page_type: 'trail detail page'
          });
          if (!user) {
            setIsSignUpModalOpen(true);
          } else {
            openTrailMap();
          }
        }}
        text={isShareCtaTreatment || isSaveCtaTreatment ? <FormattedMessage defaultMessage="Map" /> : <FormattedMessage defaultMessage="Open map" />}
      />
      {isDownloadModalOpen && (
        <HitTheTrailModal
          isOpen={isDownloadModalOpen}
          onCloseRequest={() => {
            setIsDownloadModalOpen(false);
          }}
          title={<FormattedMessage defaultMessage="Download" />}
          trailDetails={trailDetails}
          trailPage={trailPage}
        />
      )}
      {isSignUpModalOpen && (
        <SignUpModal
          closeModal={() => setIsSignUpModalOpen(false)}
          deeplinkData={{ pageId: trailDetails.id, pageType: 'trail' }}
          isOpen={isSignUpModalOpen}
          onSuccess={() => {
            setIsSignUpModalOpen(false);
            openTrailMap();
          }}
          trigger="Hit the Trail Card Open Map"
          upsellTrigger="Hit the Trail Card Open Map"
        />
      )}
      {isShareModalOpen && (
        <ShareModal
          isOpen
          onClose={() => setIsShareModalOpen(false)}
          onShareOptionClick={(shareOption: TrailDetailsShareOption) => {
            logTrailDetailsShareThisTrailOptionClicked({ share_option_clicked: shareOption, trail_id: trailDetails.id.toString() });
          }}
          shareObject={shareObject}
        />
      )}
      {listModal}
      {signUpModal}
    </div>
  );
}
